import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { withScope, captureException } from '@sentry/angular';

import { UserService } from '_services/user.service';
import { SessionService, Session } from '_services/session.service';
import { ResultService } from '_services/result.service';
import { QuestionService } from '_services/question.service';
import { QuestionsService } from '_services/questions.service';
import { UnivPrefsService } from '_services/univ-prefs.service';
import { EclService } from '_services/ecl.service';
import { BehaviorSubject } from 'rxjs';
import { tap, take } from 'rxjs/operators';
import { Result, AdminResult } from '_models/result';
import { environment } from '../../environments/environment';

@Injectable()
export class WelcomeService {
  private sessionService = inject(SessionService);
  private resultService = inject(ResultService);
  private questionService = inject(QuestionService);
  private qCloud = inject(QuestionsService);
  private userService = inject(UserService);
  ups = inject(UnivPrefsService);
  private ecl = inject(EclService);
  private router = inject(Router);


  public errorMsg = new BehaviorSubject<string | null>(null);
  public sessionId = '';
  // Set by ResumeComponent on return from College Board login.
  public pendingSessionId = '';
  public resumeRoute = '';
  public sessionState = new BehaviorSubject<'none' | 'loading' | 'complete' | 'incomplete' | 'missing'>('none');

  public route: ActivatedRoute | null = null;

  loadSchool(schoolId: string) {
    this.userService.isDemo = schoolId === 'faber';
    this.userService.isAidQuiz = schoolId === 'aidquiz';
    // Admin preview does not use student ECL / College Board login.
    if (!this.userService.isAdmin) {
      this.ecl.initiateEcl();
    }
    return this.ups.getPreferences$(schoolId, this.userService.isAdmin).pipe(
      tap(
        success => {
          if (success) {
            const status = this.ups.preferences()?.status;
            // If any status is set then navigate to status page with status code.
            if (status && !this.userService.isAdmin) {
              // Stop here: continuing would let checkForSession navigate again
              // and race the user straight back off the status page.
              this.router.navigate(['status']);
              return;
            }
            if (!this.questionService.isLoaded()) {
              this.getCloudQuestions(schoolId);
            } else {
              // Questions already in memory, so there is nothing to load — but
              // checkForSession still has to run. It used to live only inside
              // getCloudQuestions's then(), so this path silently skipped the
              // session restore (and left the resume spinner up forever).
              this.checkForSession();
            }
          } else {
            console.warn('No preferences found for school: ', schoolId);
            // Raven.captureException('No preferences found for school: ' + schoolId);
            this.router.navigate(['/app']);
          }
        },
        (err) => {
          console.log('Error while loading preferences for school: ', schoolId, ' Code:', err.code, ' Message:', err.message);
          // if (err.code === 'permission-denied') { This is most likely the IE 11.0 issue }
          withScope(scope => {
            scope.setTag('Firebase', 'GetPreferences');
            captureException(err);
          });
          this.router.navigate(['/app']);
        })
    );
  }

  private getCloudQuestions(schoolId: string) {
    this.qCloud.loadQuestions(schoolId, this.userService.isAdmin)
      .then(_res => {
        this.questionService.processQuestions();
        this.checkForSession();
      })
      .catch(err => {
        console.log(err);
        this.errorMsg.next('The servers seem to be unreachable, please try again later or from a stronger internet connection.');
        withScope(scope => {
          scope.setTag('Firebase', 'LoadQuestions');
          captureException(err);
        });
      });
  }

  /**
   * Begin a College Board login from anywhere in the calculator. Snapshots
   * the current answers to a pending session (only when there is something to
   * save), then redirects to CB with a DURL that returns to the resume route.
   * In mock environments the CB round-trip is skipped entirely.
   *
   * `autoRedirect` only matters in the mock env: when false, the pending session
   * is still created and the return URL is logged, but the browser does not
   * navigate — so you can inspect the backend, then drive the resume manually.
   */
  async startCbLogin(autoRedirect = true) {
    if (this.userService.isAdmin) {
      return;
    }

    const hasAnswers = this.questionService.hasAnswersWorthSaving();

    let durl: string;
    if (hasAnswers) {
      const resumeRoute = this.currentResumeRoute();
      try {
        const pendingId = await this.sessionService.storePendingSession(
          this.questionService.answers(),
          this.resultService.result.value ?? undefined
        );
        durl = this.buildResumeDurl(pendingId, resumeRoute);
      } catch (err) {
        // Couldn't snapshot — fall back to a plain login; arrival restore still runs.
        console.error('Failed to snapshot answers before login; continuing with plain login', err);
        durl = this.buildStartDurl();
      }
    } else {
      // Nothing to lose — no pending doc; the normal arrival flow restores prior sessions.
      durl = this.buildStartDurl();
    }

    this.redirectToLogin(durl, autoRedirect);
  }

  /** Current calculator section, used as the resume route on return. */
  private currentResumeRoute(): string {
    const path = this.router.url.split('#')[0].split('?')[0];
    const segments = path.split('/').filter(Boolean); // e.g. ['app', schoolId, section]
    const last = segments[segments.length - 1];
    if (!last || last === 'app' || last === this.userService.schoolId) {
      return this.userService.isAidQuiz ? 'household-information' : 'dependency';
    }
    return last;
  }

  private origin(): string {
    return window.location.protocol + '//' + window.location.host;
  }

  private buildResumeDurl(pendingId: string, resumeRoute: string): string {
    return `${this.origin()}/app/${this.userService.schoolId}/resume/${pendingId}?resumeRoute=${resumeRoute}`;
  }

  private buildStartDurl(): string {
    const route = this.userService.isAidQuiz ? 'household-information' : 'dependency';
    return `${this.origin()}/app/${this.userService.schoolId}/${route}`;
  }

  private redirectToLogin(durl: string, autoRedirect = true) {
    if (environment.allowMockEclLogin) {
      // Mock env: skip College Board entirely and go straight to the return URL.
      const sep = durl.includes('?') ? '&' : '?';
      const mockUrl = `${durl}${sep}fakeLogin=student`;
      // Dev aid: only a resume URL carries a pending session id. Log it so you
      // can copy it and swap fakeLogin=student for professional / studentNoPid.
      if (durl.includes('/resume/')) {
        console.log('[mock ECL] return URL:', mockUrl);
      }
      // Manual stop (autoRedirect=false, e.g. the Fake Login button): the pending
      // session is already created above, so you can inspect the backend before
      // driving the resume/claim yourself — paste the logged URL to continue.
      if (autoRedirect) {
        window.location.href = mockUrl;
      }
      return;
    }
    if (this.ecl.newEclFound.value) {
      window.location.href = this.ecl.getLoginUrlForDurl(durl);
    } else {
      alert('The College Board login is not presently connected.  Please try again in 24 hours.');
    }
  }

  private checkForSession() {
    // A pending claim outranks every other branch, including the demo school:
    // the user is sitting on the resume spinner, and only the claim (or its
    // failure path) navigates them off it. Anything that returns without
    // navigating strands them there.
    if (this.pendingSessionId !== '') {
      // Returning from College Board login — claim before any other lookup so
      // getLatestForCBUID can never race it.
      this.claimPendingSession();
      return;
    }

    if (this.userService.isDemo) {
      // No Sessions for the Demo school
      this.sessionId = '';
    } else if (this.sessionId !== '') {
      this.getSpecifiedSession(this.sessionId);
    } else if (this.ecl.loggedIn()) {
      // Logged in with College Board ID.  Get Latest Session
      this.getLatestForCBUID();
    }
  }

  private claimPendingSession() {
    this.sessionService.claimPendingSession$(this.pendingSessionId)
      .pipe(take(1)).subscribe(data => {
        // Dev aid: the session promoted from the pending doc by the claim.
        console.log('[session] claimed userSession returned:', data);
        if (data) {
          const session: Session = data;
          this.userService.loadedSession = session;
          // Append-only: never write back to the claimed doc, so hold no id.
          this.userService.sessionId = '';
          this.sessionService.setAnswers(session);
          if (session.result
            && this.userService.progress === 100
            && session.univName === this.userService.schoolId) {
            this.resultService.parseResults(session.result as Result);
          }
          this.sessionState.next('complete');
          this.navigateAfterClaim();
        } else {
          // Quiet fail: pending doc missing/expired, or returned without CB login.
          this.claimFailed();
        }
      }, (err) => {
        console.log('Error while claiming pending session: ', this.pendingSessionId, ' Code:', err.code, ' Message:', err.message);
        this.claimFailed();
      });
  }

  private navigateAfterClaim() {
    // resumeRoute is not validated here: the calculator's `**` route redirects
    // anything it does not recognize to the school's first step, so an unknown
    // or tampered value costs a redirect rather than a dead end.
    this.leaveResume(this.resumeRoute || this.startRoute());
  }

  private claimFailed() {
    this.sessionId = '';
    this.sessionState.next('missing');
    this.leaveResume(this.startRoute());
  }

  private startRoute(): string {
    return this.userService.isAidQuiz ? 'household-information' : 'dependency';
  }

  /**
   * Get off the resume spinner, whatever it takes. A cancelled or rejected
   * navigation would leave the user watching it indefinitely, so retry once at
   * the school's first step and let the component's watchdog handle the rest.
   */
  private leaveResume(route: string) {
    const fallback = this.startRoute();
    this.pendingSessionId = '';
    this.resumeRoute = '';
    // Absolute navigation naturally strips the /resume/:id segment from the URL.
    this.router.navigate(['/app', this.userService.schoolId, route])
      .then(ok => {
        if (!ok && route !== fallback) {
          return this.router.navigate(['/app', this.userService.schoolId, fallback]);
        }
        return ok;
      })
      .catch(err => {
        console.error('Navigation off the resume route failed:', err);
      });
  }

  private getSpecifiedSession(sessionId: string) {
    this.sessionService.getSessionFromCloud$(sessionId)
      .pipe(take(1)).subscribe(data => {
        // data will be undefined if the session is not found
        if (data) {
          const session: Session = data;
          this.userService.sessionId = sessionId;
          this.userService.loadedSession = session;
          this.userService.cbUserId = session.cbUserId || 'guest';
          this.loadSessionAnswersAndResults(session, sessionId);
        } else {
          console.log('Bad session id: ', sessionId);
          this.sessionState.next('missing');
          this.sessionId = '';
        }
      }, (err) => {
        console.log('Error while loading sessionID: ', sessionId, ' Code:', err.code, ' Message:', err.message);
        this.sessionId = '';
      });
  }

  private getLatestForCBUID() {
    this.sessionService.getSessionFromCloud$(null, this.userService.cbUserId)
      .pipe(take(1)).subscribe(data => {
        // data will be [] if no sessions are found
        if (data) {
          const session: Session = data;
          this.userService.loadedSession = session;
          // Append-only: the function returns data only (no id). Never write
          // back to a restored doc, so keep sessionId '' (was becoming undefined
          // via data.id and could reach doc(collection, undefined)).
          this.userService.sessionId = '';
          this.loadSessionAnswersAndResults(session, '');
        } else {
          console.log('No sessions exist for logged in college board user');
          this.sessionState.next('none');
          this.sessionId = '';
        }
      }, (err) => {
        console.log('Error while loading session for CBUID: ', this.userService.cbUserId, ' Code:', err.code, ' Message:', err.message);
        this.sessionId = '';
      });
  }

  private loadSessionAnswersAndResults(session: Session, sessionId: string) {
    this.sessionService.setAnswers(session);
    const sameSchool = session.univName === this.userService.schoolId;
    // Cross-school is prefill-only: hydrate overlapping answers for reuse, but
    // never hydrate results or auto-navigate — even if progress computes to 100%
    // (auto-navigating cross-school showed an empty results page).
    if (this.userService.progress === 100 && sameSchool) {
      if (session.result) {
        if (this.userService.isAdmin && (session.result as AdminResult).npcResult) {
          this.resultService.parseResults((session.result as AdminResult).npcResult);
        } else {
          this.resultService.parseResults(session.result as Result);
        }
      }
      const route = this.userService.isAidQuiz ? './sai-results' : './results';
      this.router.navigate([route], { relativeTo: this.route });
      this.sessionState.next('complete');
    } else {
      if (!sameSchool) {
        console.log('Cross-school session: prefilled answers only, no navigate: ', sessionId);
      }
      this.sessionState.next('incomplete');
      this.sessionId = '';
    }
  }
}
